"use client"

import { useCallback, useEffect, useState, type ReactNode } from "react"
import { createClient } from "@/utils/supabase/client"
import { ChatCheckoutDialog } from "@/components/chat/ChatCheckoutDialog"
import { ChatListingDetailSheet } from "@/components/chat/ChatListingDetailSheet"
import { DockSheet } from "@/components/ui/dock-sheet"
import { DialogCard } from "@/components/ui/dialog-card"
import { SHEET_Z_NESTED } from "@/lib/sheet-styles"
import { useMobile } from "@/hooks/use-mobile"
import { cartEvents } from "@/lib/optimized-cart-events"
import { releaseCart, removeListingFromCart } from "@/lib/cart-quick-checkout"

type SheetFlow = {
  listingId: string
  messageId: string
  roomName: string
  addedInThisFlow: boolean
  step: "detail" | "checkout"
} | null

export function GlobalQuickCheckout() {
  const isMobile = useMobile()
  const [userId, setUserId] = useState<string | null>(null)
  const [sheetFlow, setSheetFlow] = useState<SheetFlow>(null)
  const [detailFooter, setDetailFooter] = useState<ReactNode>(null)
  const [detailBelowTitle, setDetailBelowTitle] = useState<ReactNode>(null)

  useEffect(() => {
    if (sheetFlow?.step !== "detail") {
      setDetailFooter(null)
      setDetailBelowTitle(null)
    }
  }, [sheetFlow?.step])

  useEffect(() => {
    const supabase = createClient()
    supabase.auth.getUser().then(({ data: { user } }) => setUserId(user?.id ?? null))
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null)
    })
    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { listingId?: string; messageId?: string; roomName?: string; addedInThisFlow?: boolean } | undefined
      const listingId = typeof detail?.listingId === "string" ? detail.listingId : ""
      if (!listingId) return
      const messageId = typeof detail?.messageId === "string" ? detail.messageId : ""
      setSheetFlow({
        listingId,
        messageId,
        roomName: typeof detail?.roomName === "string" ? detail.roomName : "",
        addedInThisFlow: Boolean(detail?.addedInThisFlow),
        step: messageId ? "checkout" : "detail",
      })
    }
    window.addEventListener("open-chat-checkout", handler as EventListener)
    return () => window.removeEventListener("open-chat-checkout", handler as EventListener)
  }, [])

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { listingId?: string; messageId?: string; roomName?: string } | undefined
      const listingId = typeof detail?.listingId === "string" ? detail.listingId : ""
      if (!listingId) return
      if (!detail?.messageId) {
        setSheetFlow((prev) =>
          prev?.listingId === listingId
            ? { ...prev, step: "detail" }
            : {
                listingId,
                messageId: "",
                roomName: "",
                addedInThisFlow: false,
                step: "detail",
              },
        )
      }
    }
    window.addEventListener("chat-checkout-back", handler as EventListener)
    return () => window.removeEventListener("chat-checkout-back", handler as EventListener)
  }, [])

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { listingId?: string } | undefined
      const listingId = typeof detail?.listingId === "string" ? detail.listingId : ""
      if (!listingId) return
      setSheetFlow({
        listingId,
        messageId: "",
        roomName: "",
        addedInThisFlow: false,
        step: "detail",
      })
    }
    window.addEventListener("open-feed-buy-now-sheet", handler as EventListener)
    return () => window.removeEventListener("open-feed-buy-now-sheet", handler as EventListener)
  }, [])

  const closeSheet = useCallback(async () => {
    const id = sheetFlow?.listingId
    const added = sheetFlow?.addedInThisFlow ?? false
    setSheetFlow(null)
    setDetailFooter(null)
    setDetailBelowTitle(null)
    if (id) {
      await releaseCart({ listingIds: [id] }).catch(() => {})
      if (added && userId) {
        await removeListingFromCart({ userId, listingId: id }).catch(() => {})
      }
      cartEvents.emitCartRefresh()
    }
  }, [sheetFlow, userId])

  const goCheckoutBack = useCallback(() => {
    if (!sheetFlow || sheetFlow.step !== "checkout") return
    if (sheetFlow.messageId) {
      void closeSheet()
      return
    }
    setSheetFlow({ ...sheetFlow, step: "detail" })
  }, [sheetFlow, closeSheet])

  const sheetOpen = Boolean(userId && sheetFlow)
  const step = sheetFlow?.step ?? "detail"
  const listingId = sheetFlow?.listingId ?? ""

  const checkoutBody = sheetFlow && step === "checkout" ? (
    <ChatCheckoutDialog
      embedded
      open
      onOpenChangeAction={(open) => { if (!open) void closeSheet() }}
      userId={userId!}
      listingId={listingId}
      roomName={sheetFlow.roomName}
      messageId={sheetFlow.messageId}
      addedInThisFlow={sheetFlow.addedInThisFlow}
      hideShippingSelection
    />
  ) : null

  const detailSheet = sheetFlow && step === "detail" ? (
    <ChatListingDetailSheet
      embedded
      isOpen
      listingId={listingId}
      onOpenChange={() => {}}
      currentUserId={userId}
      otherParticipantId={null}
      messageId={sheetFlow.messageId}
      roomName={sheetFlow.roomName}
      hideReport={!sheetFlow.messageId}
      onFooterChange={setDetailFooter}
      onFixedSectionChange={setDetailBelowTitle}
      onProceedToCheckout={({ addedInThisFlow }) => {
        setSheetFlow((prev) => (prev ? { ...prev, step: "checkout", addedInThisFlow } : null))
      }}
    />
  ) : null

  return (
    <>
      {sheetOpen ? (
        isMobile ? (
          <DockSheet
            open={sheetOpen}
            onOpenChange={(open) => {
              if (!open) void closeSheet()
            }}
            title={step === "detail" ? "Buy now" : "Checkout"}
            titleAlign={step === "checkout" ? "left" : "center"}
            zIndex={SHEET_Z_NESTED}
            onBack={step === "checkout" ? goCheckoutBack : undefined}
            belowTitle={step === "detail" ? detailBelowTitle : undefined}
            footer={step === "detail" ? detailFooter : undefined}
            contentClassName={step === "checkout" ? "px-0 pt-0 scrollbar-hide" : undefined}
          >
            {step === "detail" ? detailSheet : checkoutBody}
          </DockSheet>
        ) : /* One surface, two chromes by `step` — the shape §8 finding 2
               describes. The props are dynamic, the primitive is not: both
               variants paint the same `p-4` row with the same 36px control,
               so swapping steps does not shift the body. `detail` has no
               chrome title because the listing hero inside `detailSheet` is
               the heading; `checkout` gets back + title, matching the sheet
               twin above prop-for-prop.

               The legacy inline fetches (FRONTEND_WIRING_STRATEGY §3.2 G5-G6)
               live in the children, not here — this change is chrome only and
               deliberately does not touch them. */
        step === "detail" ? (
          <DialogCard
            open={sheetOpen}
            onOpenChange={(open) => {
              if (!open) void closeSheet()
            }}
            chrome="closeOnly"
            a11yTitle="Buy now"
            panelClassName="h-[min(90dvh,720px)] max-h-[min(90dvh,720px)] max-w-md shadow-2xl"
            footer={detailFooter}
          >
            {detailSheet}
          </DialogCard>
        ) : (
          <DialogCard
            open={sheetOpen}
            onOpenChange={(open) => {
              if (!open) void closeSheet()
            }}
            title="Checkout"
            titleAlign="left"
            onBack={goCheckoutBack}
            panelClassName="h-[min(90dvh,720px)] max-h-[min(90dvh,720px)] max-w-md shadow-2xl"
            /* No `footer`: the embedded checkout keeps its payment rails
               inline in the scroll body — the documented exception in
               OVERLAY_FOOTER.md §"feed / chat quick checkout". */
            contentClassName="px-0 pt-0"
          >
            {checkoutBody}
          </DialogCard>
        )
      ) : null}
    </>
  )
}
